import { useCallback, useMemo, useState } from 'react';
import { useTranslate } from '@providers/TranslateProvider';

import { ChessBoardProps } from '@exports/ChessBoard';
import { useArrows } from '@exports/ChessGame';
import { useBoardSounds } from '@exports/hooks/useBoardSounds';
import { useBoardTheme } from '@exports/hooks/useBoardTheme';

import {
  useClaimVictoryMutation,
  useGameQuery,
  useResignMutation,
} from '@features/shared/api/game';
import { useStreamsGameQuery } from '@features/shared/api/streams';

import { IFastGame } from '../types/fast-game';
import { IMove } from '../types/game';
import { formatGameSpecs } from '../utils/formatGameSpecs';
import { getSides } from '../utils/getSides';
import { mapGameWinner } from '../utils/mapGameWinner';
import { useAutoPromotion } from './useAutoPromotion';
import { useDrawOffer } from './useDrawOffer';
import { useGameErrorToast } from './useGameErrorToast';
import { useGameModal } from './useGameModal';
import { useGracePeriodOverdue } from './useGracePeriodOverdue';
import { useRematch } from './useRematch';
import { useRotateBoard } from './useRotateBoard';
import { useScoresheet } from './useScoresheet';

export function useFastGame(playerId?: string, fastGame?: IFastGame) {
  const { lang, t } = useTranslate();

  // ------------------------------------------
  // Game Query

  const gameId = fastGame?.gameId;
  const isInited = !!fastGame;
  const isLive = fastGame && !fastGame.isFinished;

  const slowGameQuery = useStreamsGameQuery(gameId ?? '', {
    staleTime: Infinity,
    enabled: isInited && !isLive,
  });
  const fastGameQuery = useGameQuery(gameId ?? '', {
    staleTime: Infinity,
    customAccessToken: fastGame?.customToken,
    enabled: isInited && isLive,
  });

  const {
    data: game,
    isPending: isGamePending,
    refetch: onRefetchGame,
  } = isLive ? fastGameQuery : slowGameQuery;

  // ------------------------------------------
  // Statuses

  const winner = mapGameWinner(game?.result);
  const isInProgress = game?.status === 'InProgress';
  const isFinished = game?.status === 'Finished';

  // ------------------------------------------
  // Sides

  const { playerSide, opponentSide } = getSides(playerId, game);
  const side = playerSide === 'none' ? 'white' : playerSide;
  const isPlayer = playerSide !== 'none';

  // ------------------------------------------
  // Board & Colors

  const { rotated, remapColor, invertColor } = useRotateBoard();
  const boardOrientation = remapColor(side);
  const player1Color = invertColor(remapColor(side));
  const player2Color = remapColor(side);

  // ------------------------------------------
  // Errors Toast

  const { toast, showToast: _showToast } = useGameErrorToast();
  const onError = useCallback(
    (_error: Error) => {
      _showToast(
        <>
          <b>{t('@features/game/pages/GamePage/Error Header')}</b>.{' '}
          {t('@features/game/pages/GamePage/Error Text')}
        </>,
      );
    },
    [_showToast, t],
  );

  // ------------------------------------------
  // Draw

  const { canRequestDraw, showDrawOffer, onDrawAccept, onDrawDecline, onDrawOffer } = useDrawOffer({
    game,
    customAccessToken: fastGame?.customToken,
    playerId,
    onError,
  });

  // ------------------------------------------
  // Resign

  const canResign = isPlayer && game && game.moves.length >= 2 && isInProgress;
  const resignMutation = useResignMutation({
    customAccessToken: fastGame?.customToken,
  });
  const onResign = useCallback(() => {
    if (!gameId) return;
    resignMutation.mutate(gameId, { onError });
  }, [gameId, onError, resignMutation]);

  // ------------------------------------------
  // Claim Victory

  const canClaimVictory = useGracePeriodOverdue(
    opponentSide !== 'none' && isInProgress
      ? game?.[opponentSide].offlineGracePeriodTillTimestampMs
      : undefined,
    5000,
  );
  const claimVictoryMutation = useClaimVictoryMutation({
    customAccessToken: fastGame?.customToken,
  });
  const hasGracePeriod = (color: 'white' | 'black') => {
    const till = game?.[color].offlineGracePeriodTillTimestampMs;
    return till !== undefined && till !== null;
  };
  const onClaimVictory = useCallback(() => {
    if (!gameId) return;
    claimVictoryMutation.mutate(gameId, { onError });
  }, [claimVictoryMutation, gameId, onError]);

  // ------------------------------------------
  // Network problems (from ServerGameWidget)

  const [networkProblems, onNetworkProblems] = useState(false);
  const isPlayer1Offline = isInProgress && hasGracePeriod(player1Color);
  const isPlayer2Offline = isInProgress && hasGracePeriod(player2Color);

  // ------------------------------------------
  // Rematch

  const { rematchStatus, onRematch } = useRematch(fastGame, playerSide);

  // ------------------------------------------
  // Game info

  const gameSpecs = useMemo(
    () => formatGameSpecs(game, remapColor(side), t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [game?.id, side, lang],
  );

  // ------------------------------------------
  // Scoresheet

  const scoresheet = useScoresheet({
    game,
    boardProps: { winner } as ChessBoardProps,
    autoResetOnMove: isLive,
  });
  const customPlayersTimeMs =
    !scoresheet.isLastMove || isFinished ? scoresheet.playersTimeMs : undefined;

  const selected = scoresheet.props.selected;
  const selectedIndex = game?.moves.indexOf(selected as IMove) ?? -1;
  const historyMove = selectedIndex >= 0 ? selectedIndex : undefined;

  const historyFen =
    historyMove !== undefined && game ? game.moves[historyMove]?.fenAfter : undefined;

  // ------------------------------------------
  // Arrows

  const { arrows, onUserArrow } = useArrows(historyFen ?? game?.currentFen ?? '');

  // ------------------------------------------
  // Game modal

  const modal = useGameModal(gameId, networkProblems, game);

  // ------------------------------------------
  // Analysis

  const canShowAnalysis = isFinished && game.result !== 'NotPlayed';
  const [showAnalysis, setShowAnalysis] = useState<boolean | null>(null);
  const onOpenAnalysis = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    setShowAnalysis(true);
  }, []);
  const onCloseAnalysis = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    setShowAnalysis(false);
  }, []);

  // ------------------------------------------
  // Turns

  const turn: 'black' | 'white' | null = isInProgress
    ? game?.nextMoveBy === 'Black'
      ? 'black'
      : 'white'
    : null;

  const isTopFirstMove =
    game && game.moves.length <= (invertColor(remapColor(side)) === 'white' ? 0 : 1);
  const isBottomFirstMove = game && game.moves.length <= (remapColor(side) === 'white' ? 0 : 1);

  // ------------------------------------------
  // Settings

  const { boardTheme } = useBoardTheme();
  const { autoPromotion } = useAutoPromotion();
  const { boardSounds } = useBoardSounds();

  // ------------------------------------------
  // Return

  return {
    game,
    isGamePending,
    onRefetchGame,
    //
    isInProgress,
    isFinished,
    winner,
    //
    side,
    playerSide,
    opponentSide,
    isPlayer,
    //
    rotated,
    boardOrientation,
    player1Color,
    player2Color,
    //
    toast,
    onError,
    //
    canRequestDraw,
    showDrawOffer,
    onDrawAccept,
    onDrawDecline,
    onDrawOffer,
    //
    canResign,
    onResign,
    //
    canClaimVictory,
    onClaimVictory,
    //
    isPlayer1Offline,
    isPlayer2Offline,
    onNetworkProblems,
    //
    rematchStatus,
    onRematch,
    //
    gameSpecs,
    //
    scoresheet,
    customPlayersTimeMs,
    historyMove,
    //
    arrows,
    onUserArrow,
    //
    modal,
    //
    canShowAnalysis,
    showAnalysis,
    onOpenAnalysis,
    onCloseAnalysis,
    //
    turn,
    isTopFirstMove,
    isBottomFirstMove,
    //
    boardTheme,
    autoPromotion,
    boardSounds,
  };
}
